import {
  CnpjFormField,
  FloatingFormItem,
  InputDecimal,
  InputText,
} from '@/components/_common';
import CfopSelectField from '@/components/_common/CfopSelectField';
import NcmSelectField from '@/components/_common/NcmSelectField';
import {
  InvoiceItemFormValues,
  RelevanceScaleIndicatorEnum,
  TaxRegimeEnum,
} from '@/core/domain/invoice/invoiceNoteItems/invoiceNoteItemsParts/InvoiceNoteItems.types';
import '@/core/domain/operational/Ncm/Ncm.hooks';
import { useDefaultEmptyFieldError } from '@/hooks';
import Authorities from '@/types/Authority';
import { Col, Form, FormInstance, Input, Row, Select } from 'antd';
import CestFormField from '@/components/_common/CestFormField';
import { useInvoiceFormContext } from '@/components/Fiscal/Invoice/InvoiceFormProvider';
import { InvoiceOriginReferenceEntity } from '@/core/domain/invoice/invoices/invoices.enums';
import { hasPermissions } from '@/utils/functions/hasPermissions';

interface NoteItemsGeneralTabsFormProps {
  invoiceType: string;
  form: FormInstance<InvoiceItemFormValues>;
  changeInvoiceItemData: (
    ncmId: number,
    taxRegime: TaxRegimeEnum,
  ) => Promise<void>;
}

export default function NoteItemsGeneralNfeTabsForm({
  form,
  invoiceType,
  changeInvoiceItemData,
}: NoteItemsGeneralTabsFormProps) {
  const defaultEmptyFieldError = useDefaultEmptyFieldError();

  const { invoice } = useInvoiceFormContext();

  const entity =
    invoice.data?.originReference && invoice.data?.originReference[0]?.entity;

  const partId = Form.useWatch('partId', form);

  const watchTaxRegime = Form.useWatch(['itemTaxes', 'taxRegime'], form);

  const scaleIndicatorOptions = [
    {
      value: RelevanceScaleIndicatorEnum.producedOnARelevantScale,
      label: 'S - Produzido em Escala Relevante',
    },
    {
      value: RelevanceScaleIndicatorEnum.producedAtNonRelevantScale,
      label: 'N - Produzido em Escala NÂO Relevante',
    },
  ];

  const handleValuesChanged = async (
    changedValues: InvoiceItemFormValues,
    fieldsValues: InvoiceItemFormValues,
  ) => {
    const { quantity, valueUnitary, valueDiscount } = fieldsValues;

    const totalAmout =
      (quantity ?? 0) * (valueUnitary ?? 0) - (valueDiscount ?? 0);

    form.setFieldsValue({ valueTotal: totalAmout });

    if (changedValues?.itemTaxes?.taxRegime && fieldsValues.ncmId) {
      await changeInvoiceItemData(
        fieldsValues.ncmId,
        changedValues?.itemTaxes?.taxRegime,
      );
    }
  };

  return (
    <Form form={form} onValuesChange={handleValuesChanged}>
      <Form.Item hidden name="partId" />
      <Form.Item hidden name="consumableId" />
      <Form.Item hidden name="taskId" />

      <Row gutter={[12, 12]}>
        <Col xs={24} sm={24} md={24} lg={12}>
          <FloatingFormItem
            name="description"
            label="Descrição"
            rules={[defaultEmptyFieldError]}
          >
            <Input />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={24} md={12} lg={2}>
          <FloatingFormItem
            name="unitMeasurement"
            label="Unidade"
            rules={[defaultEmptyFieldError]}
          >
            <Input />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={24} md={12} lg={7}>
          <FloatingFormItem
            name="ncmId"
            label="NCM"
            rules={[defaultEmptyFieldError]}
            getValueFromEvent={(value) => {
              if (value) {
                changeInvoiceItemData(value, watchTaxRegime);
              }
              return value;
            }}
          >
            <NcmSelectField
              queryFilter={{ active: true }}
              disabled={!hasPermissions([Authorities.REGISTER_NCM_VIEW])}
            />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={24} md={12} lg={3}>
          <CestFormField required={false} fieldName="cest" />
        </Col>

        <Col xs={24} sm={24} md={12} lg={16}>
          <FloatingFormItem
            name="cfopId"
            label="CFOP"
            rules={[defaultEmptyFieldError]}
          >
            <CfopSelectField />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={24} md={12} lg={4}>
          <FloatingFormItem
            name="quantity"
            label="Quantidade"
            rules={[defaultEmptyFieldError]}
          >
            <InputDecimal
              disabled={entity === InvoiceOriginReferenceEntity.PURCHASE}
            />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={24} md={12} lg={4}>
          <FloatingFormItem
            name="valueUnitary"
            label="Preço unitário"
            rules={[defaultEmptyFieldError]}
          >
            <InputDecimal />
          </FloatingFormItem>
        </Col>

        {invoiceType !== 'NFS' && !partId && (
          <Col xs={24} sm={24} md={8} lg={4}>
            <FloatingFormItem name="barCode" label="Código de barras">
              <Input />
            </FloatingFormItem>
          </Col>
        )}

        <Col xs={24} sm={24} md={8} lg={4}>
          <FloatingFormItem
            name="otherValue"
            label="Valor Despesas"
            initialValue={0}
          >
            <InputDecimal />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={24} md={8} lg={4}>
          <FloatingFormItem
            name="valueDiscount"
            label="Desconto"
            initialValue={0}
          >
            <InputDecimal />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={24} md={8} lg={4}>
          <FloatingFormItem name="freightValue" label="Valor frete">
            <InputDecimal />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={24} md={8} lg={4}>
          <FloatingFormItem name="insuranceValue" label="Valor seguro">
            <InputDecimal />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={24} md={8} lg={4}>
          <FloatingFormItem
            name="valueTotal"
            label="Valor total"
            initialValue={0}
          >
            <InputDecimal disabled />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={24} md={24} lg={12}>
          <FloatingFormItem
            name="relevanceScaleIndicator"
            label="Indicador de escala relevante"
          >
            <Select options={scaleIndicatorOptions} />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={24} md={12} lg={6}>
          <CnpjFormField
            label="CNPJ do fabricante"
            fieldName="manufacturerMerchandiseCnpj"
            required={false}
          />
        </Col>

        <Col xs={24} sm={24} md={12} lg={6}>
          <FloatingFormItem name="taxBenefitCode" label="Cód. benefício fiscal">
            <Input />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={24} md={12} lg={5} xxl={4}>
          <FloatingFormItem
            name="approximateTaxValue"
            label="Valor aproximado total de tributos"
            initialValue={0}
          >
            <InputDecimal />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={24} md={12} lg={5} xxl={4}>
          <FloatingFormItem
            name="purchaseNumber"
            label="Número do Pedido de Compra"
          >
            <InputText onlyNumber />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={24} md={8} lg={5} xxl={4}>
          <FloatingFormItem
            name="purchaseOrder"
            label="Item do Pedido de Compra"
          >
            <Input />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={24} md={8} lg={5}>
          <FloatingFormItem name="nve" label="Nve">
            <Input />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={24} md={8} lg={4}>
          <FloatingFormItem name="exTipi" label="Ex Tipi">
            <Input />
          </FloatingFormItem>
        </Col>
      </Row>
    </Form>
  );
}
